/**
 * MobileTodoDrawer.js
 * 모바일 뷰용 To-Do 하단 Drawer 컴포넌트
 * 스와이프 업으로 열리는 To-Do 리스트
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  Chip,
  SwipeableDrawer,
  Checkbox,
} from '@mui/material';
import { ChevronUp, CheckSquare, Clock } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import axios from 'axios';
import { format } from 'date-fns';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const MobileTodoDrawer = ({ 
  open, 
  onOpen, 
  onClose,
  onTodoDragStart,
  onTodoClick,
  onTodoSelect,
}) => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTodos, setSelectedTodos] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);

  // 업체 목록 조회
  const fetchCompanies = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/customers`);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('업체 목록 조회 실패:', error);
    }
  }, []);

  // 계약 목록 조회
  const fetchContracts = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/recent`, {
        params: { limit: 20 } // 최대 20개 계약 조회 (API 제한)
      });
      setContracts(response.data || []);
    } catch (error) {
      console.error('계약 목록 조회 실패:', error);
    }
  }, []);

  // To-Do 리스트 조회
  const fetchTodos = React.useCallback(async () => {
    if (!isMobileView) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/todos`, {
        params: { status: 'active' }
      });
      setTodos(response.data || []);
    } catch (error) {
      console.error('To-Do 리스트 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [isMobileView]);

  useEffect(() => {
    if (open && isMobileView) {
      fetchCompanies();
      fetchContracts();
      fetchTodos();
    }
  }, [open, isMobileView, fetchCompanies, fetchContracts, fetchTodos]);

  // To-Do 완료 처리
  const handleTodoComplete = async (todo) => {
    try {
      await axios.put(`${API_BASE_URL}/todos/${todo.todo_id}`, {
        status: 'completed'
      });
      fetchTodos();
    } catch (error) {
      console.error('To-Do 완료 처리 실패:', error);
    }
  };

  // To-Do 체크박스 선택 처리
  const handleTodoCheckboxChange = (todoId, checked) => {
    if (checked) {
      setSelectedTodos(prev => [...prev, todoId]);
      const todo = todos.find(t => t.todo_id === todoId);
      if (todo && onTodoSelect) {
        onTodoSelect(todo);
      }
    } else {
      setSelectedTodos(prev => prev.filter(id => id !== todoId));
    }
  };

  return (
    <>
      {/* FAB 버튼 제거 - 하단 네비게이션으로 대체 */}

      {/* Swipeable Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={open && isMobileView}
        onClose={onClose}
        onOpen={onOpen}
        disableSwipeToOpen={false}
        PaperProps={{
          sx: {
            maxHeight: '80vh',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
          },
        }}
      >
        {/* Drawer 헤더 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: `1px solid ${colors.primaryBorder}`,
            position: 'sticky',
            top: 0,
            backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckSquare size={20} style={{ color: colors.accent }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primaryText }}>
              To-Do 리스트
            </Typography>
            {todos.length > 0 && (
              <Chip 
                label={todos.length} 
                size="small" 
                sx={{ 
                  backgroundColor: colors.accent,
                  color: '#FFFFFF',
                  height: 20,
                  fontSize: '12px',
                }} 
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="large">
            <ChevronUp size={24} />
          </IconButton>
        </Box>

        {/* To-Do 목록 */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
          touchAction: 'pan-y', // 수직 스크롤만 허용
          p: 2 
        }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '12px' }}>
                로딩 중...
              </Typography>
            </Box>
          ) : todos.length > 0 ? (
            <List>
              {todos.map((todo) => (
                <React.Fragment key={todo.todo_id}>
                  <Paper
                    draggable
                    onDragStart={(e) => {
                      if (onTodoDragStart) {
                        onTodoDragStart(todo);
                      }
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onClick={() => {
                      if (onTodoClick) {
                        onTodoClick(todo);
                      }
                    }}
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
                      border: `1px solid ${colors.primaryBorder}`,
                      borderRadius: 2,
                      cursor: 'grab',
                      '&:active': {
                        cursor: 'grabbing',
                        opacity: 0.8,
                      },
                      '&:hover': {
                        backgroundColor: isDark ? '#333' : '#F0F0F0',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Checkbox
                        checked={selectedTodos.includes(todo.todo_id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleTodoCheckboxChange(todo.todo_id, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ 
                          mt: -1,
                          ml: -1,
                          padding: '4px',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: colors.primaryText, mb: 0.5, fontSize: '12px' }}>
                          {todo.work_description || todo.note_1 || '제목 없음'}
                        </Typography>
                        {(() => {
                          // 업체명칭 찾기
                          const company = todo.company_code 
                            ? companies.find(c => c.company_code === todo.company_code)
                            : null;
                          const companyName = company?.company_name_kr || todo.company_code || '';
                          
                          // 계약명칭 찾기
                          const contract = todo.contract_id
                            ? contracts.find(c => c.contract_id === todo.contract_id)
                            : null;
                          const contractTitle = contract?.contract_title || todo.contract_id || '';
                          
                          return (
                            <>
                              {companyName && (
                                <Chip 
                                  label={companyName} 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '12px',
                                    mr: 1,
                                    mb: 0.5,
                                  }} 
                                />
                              )}
                              {contractTitle && (
                                <Chip 
                                  label={contractTitle} 
                                  size="small" 
                                  sx={{ 
                                    height: 20, 
                                    fontSize: '12px',
                                    mr: 1,
                                    mb: 0.5,
                                  }} 
                                />
                              )}
                            </>
                          );
                        })()}
                        {todo.job_category && (
                          <Chip 
                            label={todo.job_category} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '12px',
                              mr: 1,
                              mb: 0.5,
                            }} 
                          />
                        )}
                        {todo.note_1 && (
                          <Typography variant="caption" sx={{ color: colors.secondaryText, display: 'block', mt: 0.5, fontSize: '12px' }}>
                            {todo.note_1}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTodoComplete(todo);
                        }}
                        sx={{ 
                          color: colors.success,
                          ml: 1,
                        }}
                      >
                        <CheckSquare size={18} />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Clock size={14} style={{ color: colors.secondaryText }} />
                      <Typography variant="caption" sx={{ color: colors.secondaryText, fontSize: '12px' }}>
                        {todo.created_at ? format(new Date(todo.created_at), 'yyyy-MM-dd') : '날짜 없음'}
                      </Typography>
                    </Box>
                  </Paper>
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CheckSquare size={48} style={{ color: colors.secondaryText, marginBottom: 16 }} />
              <Typography variant="body1" sx={{ color: colors.secondaryText, mb: 1, fontSize: '12px' }}>
                등록된 To-Do가 없습니다
              </Typography>
              <Typography variant="caption" sx={{ color: colors.tertiaryText, fontSize: '12px' }}>
                To-Do를 드래그하여 캘린더에 시간 블록으로 변환할 수 있습니다
              </Typography>
            </Box>
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default MobileTodoDrawer;

